import type { AxiosError } from 'axios'
import axios from 'axios'
import { addSentryInterceptors } from '../lib/sentry-api-interceptor'
import { authTokenCache, SessionExpiredError } from '../lib/auth-token-cache'
export const API_URL = window.env.API_URL

// export const API_URL = "http://localhost:3333/api";
export const user = '/user'
export const payments = '/payments'
export const checkRegister = '/checkRegister'
export const schedule = '/schedule'
export const metrics = '/metrics'

const $api = axios.create({
  withCredentials: true,
  baseURL: API_URL
})

$api.interceptors.request.use(async config => {
  try {
    config.headers.Authorization = await authTokenCache.getToken()
  } catch (error) {
    if (error instanceof SessionExpiredError) {
      console.error('🚨 [API_INTERCEPTOR] Session expired in request interceptor:', error.message)
      // Don't set Authorization header - this will result in 401 and trigger logout flow
    } else {
      console.error('❌ [API_INTERCEPTOR] Failed to get auth token:', error)
    }
  }

  config.headers['Access-Control-Expose-Headers'] = 'Content-Disposition'
  return config
})

$api.interceptors.response.use(undefined, async error => {
  const axiosError = error as AxiosError

  if (axiosError.response?.status === 401 && axiosError.config && !axiosError.config._retry) {
    console.warn(
      '🔄 [API_INTERCEPTOR] Received 401 error, clearing token cache and retrying request'
    )

    authTokenCache.clearCache()
    axiosError.config._retry = true

    try {
      axiosError.config.headers.Authorization = await authTokenCache.getToken()
      console.info('✅ [API_INTERCEPTOR] Successfully refreshed token, retrying request')
      return $api.request(axiosError.config)
    } catch (retryError) {
      if (retryError instanceof SessionExpiredError) {
        console.error(
          '🚨 [API_INTERCEPTOR] Session expired during retry - user needs to re-authenticate'
        )
        // Don't retry again - let the auth system handle logout
        // The 401 error will bubble up and trigger the auth flow
      } else {
        console.error(
          '❌ [API_INTERCEPTOR] Failed to retry request after token refresh:',
          retryError
        )
      }
      // Don't throw retryError - throw original 401 to maintain consistent error handling
    }
  }

  throw axiosError
})

addSentryInterceptors($api)

export default $api
